<template>
  <div
    :class="{
      'comments-addon': isCommentsActive,
      'notes-active': !isCommentsActive,
    }"
  >
    <div
      v-if="isCommentsActive"
      ref="listContainer"
      class="flex-grow comments-addon__list-wrapper"
      data-tourid="rev-view-comments-list"
    >
      <CommentsList
        v-if="comments.length"
        :highlightedCommentId="highlightedCommentId"
        :modelType="modelType"
        :modelName="modelName"
        :comments="comments"
        :canEdit="canEdit"
        :modelId="modelId"
        @reply="handleReply"
      />
      <div
        v-else
        class="flex-grow-1 d-flex flex-column text-center  align-center justify-center white"
      >
        <Button color="blueGrey" size="large" icon="mdi-comment-text-outline" />
        <p class="body-1 blue-grey--text text--darken-1">
          {{ $t('common.nothing_to_see_here') }}
        </p>
        <p class="d-flex flex-column body-2">
          <span class="caption grey--text">{{
            $t('comments.there_are_no_comments_to_show')
          }}</span>
          <span class="caption grey--text">
            {{ $t('comments.you_can_be_the_first') }}</span
          >
        </p>
      </div>
    </div>
    <div class="comments-addon__addition-wrapper">
      <slot name="activity-actions" />
      <CommentComposer
        v-if="isCommentsActive"
        ref="composer"
        :replyTo="accountToReply"
        :textareaTestId="textAreaTid"
        @submit="onSubmitComment"
      />
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import _ from 'lodash'
import mixin from '@/mixins/mixin.vue'

import CommentsList from '@/components/Core/Comments/CommentsList.vue'
import CommentComposer from './CommentComposer.vue'

import emptyStateImage from '@/assets/images/empty-states/activity_empty_state.svg'
import Button from '@/components/UI/Button'

const HIGHLIGHTED_COMMENT_QUERY_PARAM = 'highlightedComment'

export default {
  name: 'CommentsAddon',

  components: {
    CommentsList,
    CommentComposer,
    Button,
  },

  mixins: [mixin],

  props: {
    canEdit: {
      type: Boolean,
      required: false,
    },
    modelType: {
      type: String,
      required: false,
    },
    modelName: {
      type: String,
      required: true,
    },
    modelId: {
      type: String,
      required: true,
    },
    submitText: {
      type: String,
      required: false,
    },
    submitCommentTestId: {
      type: String,
      required: false,
      default: 'comment_submit_btn',
    },
    isCommentsActive: {
      type: Boolean,
      required: false,
      default: true,
    },
    isEditMode: {
      type: Boolean,
      required: true,
      default: false,
    },
    textAreaTid: {
      type: String,
      required: true,
    },
    siteId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      emptyStateImage,
      accountToReply: null,
      organizationId: this.getRouteParams().organizationId,
      highlightedCommentId: this.getRouteQuery()?.[
        HIGHLIGHTED_COMMENT_QUERY_PARAM
      ],
    }
  },

  computed: {
    ...mapState('comments', {
      comments: state => _.orderBy(state?.comments, 'createdAt', 'asc'),
    }),
  },

  async mounted() {
    await this.getCommentsByModelId({
      organizationId: this.organizationId,
      modelName: this.modelName,
      modelId: this.modelId,
    })

    this.scrollToHighlightedComment()
  },

  beforeDestroy() {
    this.resetComments()
  },

  methods: {
    ...mapActions({
      postComment: 'comments/postComment',
      getCommentsByModelId: 'comments/getCommentsByModelId',
    }),

    ...mapMutations({
      resetComments: 'comments/resetComments',
    }),

    async onSubmitComment(comment) {
      if (!comment.content) return

      this.accountToReply = null

      const payload = {
        organizationId: this.organizationId,
        modelName: this.modelName,
        modelId: this.modelId,
        comment,
      }

      try {
        const createdComment = await this.postComment(payload)
        await this.getCommentsByModelId(payload)
        this.scrollToComment(createdComment._id)
      } catch (err) {
        this.handleRequestError(err)
      }
    },

    scrollToComment(commentId) {
      const cardElement = this.$refs.listContainer.querySelector(
        `[data-comment-id='${commentId}']`
      )

      if (!cardElement) {
        return this.showWarning(this.$t('comments.not_found_comment'))
      }

      this.$vuetify.goTo(cardElement)

      this.$refs.listContainer.scrollTo({
        top: cardElement.offsetTop,
        behavior: 'smooth',
      })
    },

    scrollToHighlightedComment() {
      if (!this.highlightedCommentId) return
      this.scrollToComment(this.highlightedCommentId)
      this.replaceRoute({
        query: _.omit(this.getRouteQuery(), HIGHLIGHTED_COMMENT_QUERY_PARAM),
      })
    },

    handleReply(account) {
      this.accountToReply = { ...account }
      this.$vuetify.goTo(this.$refs.composer)
    },
  },
}
</script>

<style lang="scss" scoped>
.comments-addon {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  &__list-wrapper {
    overflow: auto;
    height: 100%;
    display: flex;
    flex-direction: column;
    background-color: map-get($grey, lighten-5);
  }

  &__addition-wrapper {
    background: $color-white;
    padding: 10px;
    border-top: 1px solid $color-blue-grey-lighten2;
    bottom: 0;
    width: 100%;
  }
}
</style>
